import { useState } from "react";
import BrandMark from "@/components/BrandMark";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import SiteFooter from "@/components/SiteFooter";
import { DEFAULT_TXN_QUEUE } from "./lib";
import { useAdminMaintenanceWindow } from "@/lib/maintenance";
import { api, formatError } from "@/lib/apiClient";
import { fmtDateTime } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, UserCog, ClipboardList, Wallet,
  AlertOctagon, LogOut, FileText, DollarSign, Settings, DatabaseBackup,
  ChevronLeft, ChevronRight, AlertTriangle,
} from "lucide-react";

/**
 * Persistent, not a toast. "Maintenance started now" scrolling past in a toast is easy to lose
 * track of on a page an admin then spends an hour on — this stays up on every admin page for as
 * long as `active` does, whether that's because the schedule hasn't ended yet or because nobody's
 * turned the override off, and offers "End now" right here so ending it doesn't require a trip
 * back to Disaster Recovery. Admin traffic itself is never blocked by a window (see
 * core/security.py::get_current_user) — this banner is what keeps that "never blocked" from also
 * meaning "easy to forget is on".
 */
function MaintenanceBanner({ status, onChange }) {
  const [busy, setBusy] = useState(false);
  if (!status?.active) return null;
  const endNow = async () => {
    setBusy(true);
    try {
      await api.put("/admin/maintenance", { override: "off" });
      toast.success("Maintenance ended");
      onChange();
    } catch (e) {
      toast.error(formatError(e?.response?.data?.detail) || "Could not end maintenance");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="admin-maintenance-banner"
         className="sticky top-0 z-20 flex flex-wrap items-center gap-3 px-4 py-2.5 bg-[#F2A65A]/15 border-b border-[#F2A65A]/30 text-[#F2A65A] text-sm">
      <AlertTriangle className="w-4 h-4 shrink-0" strokeWidth={1.5} />
      <span className="font-medium">System under maintenance</span>
      <span className="text-[#F2A65A]/80">
        — customer and advocate access is locked out{status.end_at ? ` until ${fmtDateTime(status.end_at)}` : ""}.
        {status.note ? ` "${status.note}"` : ""}
      </span>
      <Button data-testid="admin-maintenance-banner-end" size="sm" variant="outline" disabled={busy}
              className="ml-auto shrink-0 h-7 text-xs border-[#F2A65A]/40 text-[#F2A65A] hover:bg-[#F2A65A]/10"
              onClick={endNow}>
        {busy ? "Ending…" : "End now"}
      </Button>
    </div>
  );
}

const NAV = [
  { to: "/admin", label: "Analytics", icon: LayoutDashboard, testid: "admin-nav-analytics", end: true },
  { to: "/admin/users", label: "Users", icon: Users, testid: "admin-nav-users" },
  { to: "/admin/reviewers", label: "Reviewers", icon: UserCog, testid: "admin-nav-reviewers" },
  // Transactions carries sub-items because the page has always had queue tabs and they are the
  // real destinations — an admin thinks "show me what is pending", not "open Transactions, then
  // find the tab". The three here are the ones worth a menu entry; the four flavours of Pending
  // stay as in-page tabs, since choosing between them is a refinement made once you are looking at
  // the list. Each sets `?tab=`, which the page already reads.
  //
  // `link` and `to` differ here, and both are load-bearing: `to` is the bare path the active-state
  // check matches on (`pathname.startsWith`), while `link` is where the item actually navigates.
  // Clicking Transactions lands on **Pending**, which is where the work is and where an admin
  // spends the day — All Open is a wider view they choose deliberately, not the one to open on.
  {
    to: "/admin/transactions", link: "/admin/transactions?tab=pending", label: "Transactions",
    icon: ClipboardList, testid: "admin-nav-transactions",
    // `tab` rather than matching the href: the page now appends the date window to the query
    // string, so an exact `pathname + search` comparison against these links stopped matching the
    // moment a range was in the URL, and the sub-item highlight silently went dead.
    children: [
      { tab: "open", to: "/admin/transactions?tab=open", label: "All Open", testid: "admin-nav-txn-open" },
      { tab: "pending", to: "/admin/transactions?tab=pending", label: "Pending", testid: "admin-nav-txn-pending" },
      { tab: "closed", to: "/admin/transactions?tab=closed", label: "Completed", testid: "admin-nav-txn-closed" },
    ],
  },
  { to: "/admin/payments", label: "Payments", icon: Wallet, testid: "admin-nav-payments" },
  { to: "/admin/pricing", label: "Pricing", icon: DollarSign, testid: "admin-nav-pricing" },
  { to: "/admin/templates", label: "Templates", icon: FileText, testid: "admin-nav-templates" },
  { to: "/admin/disaster-recovery", label: "Disaster Recovery", icon: DatabaseBackup, testid: "admin-nav-disaster-recovery" },
  { to: "/admin/ledger", label: "Failed Ledger", icon: AlertOctagon, testid: "admin-nav-ledger" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isActive = (n) => n.end ? loc.pathname === n.to : loc.pathname.startsWith(n.to);
  // The queue currently on screen, defaulted the same way AdminTransactions defaults it. Keep the
  // two in step: if they disagree, the menu highlights one queue while the table shows another.
  const currentTab = new URLSearchParams(loc.search).get("tab") || DEFAULT_TXN_QUEUE;
  const initials = (user?.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("admin_sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed(c => {
    const next = !c;
    localStorage.setItem("admin_sidebar_collapsed", next ? "1" : "0");
    return next;
  });
  const [maintenance, refreshMaintenance] = useAdminMaintenanceWindow();

  return (
    <div className={`min-h-screen grid ${collapsed ? "md:grid-cols-[64px_1fr]" : "md:grid-cols-[240px_1fr]"} bg-[#080C16] text-[#F9F8F6] transition-[grid-template-columns] duration-200`}>
      <aside className="relative bg-[#0B111C] border-r border-white/5 p-3 md:p-5 flex flex-col sticky top-0 h-screen">
        <Link to="/admin" className="flex items-center gap-2 font-serif-display text-lg tracking-tight mb-10 overflow-hidden whitespace-nowrap">
          <BrandMark />
          {!collapsed && <>DharmaTN<span className="text-[9px] tracking-[0.24em] uppercase text-[#6B7280] ml-1">Admin</span></>}
        </Link>
        <nav className="space-y-0.5 flex-1 overflow-y-auto overflow-x-hidden">
          {NAV.map(n => (
            <div key={n.to}>
              <Link
                to={n.link || n.to} data-testid={n.testid} title={collapsed ? n.label : undefined}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${collapsed ? "justify-center" : ""} ${
                  isActive(n) ? "bg-white/10 text-white" : "text-[#A0AABF] hover:text-white hover:bg-white/5"
                }`}
              >
                <n.icon className="w-4 h-4 shrink-0" strokeWidth={1.5} /> {!collapsed && n.label}
              </Link>
              {/* Sub-items are hidden while the rail is collapsed — there is no room for them, and
                  the parent still reaches the same page. They show whenever that section is the
                  active one, rather than needing a separate expand/collapse to remember. */}
              {!collapsed && n.children && isActive(n) && (
                <div className="mt-1 mb-1 ml-4 pl-3 border-l border-white/10 space-y-0.5">
                  {n.children.map(c => {
                    const active = c.tab === currentTab;
                    return (
                      <Link
                        key={c.to} to={c.to} data-testid={c.testid}
                        className={`block px-3 py-1.5 rounded-md text-[13px] transition-colors ${
                          active ? "text-[#CBA96C]" : "text-[#6B7280] hover:text-white"
                        }`}
                      >
                        {c.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="border-t border-white/5 pt-4">
          <div className={`flex items-center gap-3 ${collapsed ? "justify-center" : ""}`}>
            <div className="w-8 h-8 rounded-full bg-[#CBA96C] text-[#080C16] flex items-center justify-center font-medium text-xs shrink-0">{initials}</div>
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{user?.name}</div>
                <div className="text-[11px] text-[#6B7280] truncate">{user?.email}</div>
              </div>
            )}
          </div>
          <button
            data-testid="admin-logout" title={collapsed ? "Sign out" : undefined}
            // Navigate first, then clear — see the signOut comment in CustomerLayout.jsx.
            onClick={async () => { nav("/", { replace: true }); await logout(); }}
            className={`mt-3 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-[#A0AABF] hover:text-white hover:bg-white/5 transition-colors ${collapsed ? "justify-center" : ""}`}
          >
            <LogOut className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} /> {!collapsed && "Sign out"}
          </button>
        </div>

        <button
          data-testid="admin-sidebar-toggle" onClick={toggleCollapsed}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="hidden md:flex items-center justify-center absolute top-6 -right-3 w-6 h-6 rounded-full bg-[#111623] border border-white/10 text-[#A0AABF] hover:text-white hover:border-white/20 transition-colors"
        >
          {collapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
        </button>
      </aside>
      <main className="overflow-x-auto min-w-0 flex flex-col">
        <MaintenanceBanner status={maintenance} onChange={refreshMaintenance} />
        <div className="p-8 md:p-10 flex-1"><Outlet /></div>
        <SiteFooter className="px-4" />
      </main>
    </div>
  );
}
